<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import { Link } from "@inertiajs/vue3";
import { route } from "../../../vendor/tightenco/ziggy/src/js";
import {
  getLocale,
  t as translationHelper,
  tWithFallback as twf,
} from "@/i18n/helper";
import LanguageSwitcher from "@/Components/LanguageSwitcher.vue";

const props = defineProps({
  menu: {
    type: Array,
    required: true,
  },
});

// State for mobile menu, sticky, modal, etc
const navigationOpen = ref(false);
const stickyMenu = ref(false);
const modalSearch = ref(false);
const modalNewsletter = ref(false);
const isMobileMenuVisible = ref(false);
const activeMobileDropdownId = ref(null);

// Mobile menu functions
const toggleMobileMenu = () => {
  isMobileMenuVisible.value = !isMobileMenuVisible.value;
  activeMobileDropdownId.value = null;
};

const toggleMobileDropdown = (id) => {
  activeMobileDropdownId.value =
    activeMobileDropdownId.value === id ? null : id;
};

const closeMobileMenu = () => {
  isMobileMenuVisible.value = false;
  activeMobileDropdownId.value = null;
};

// Handle click outside to close mobile menu
const handleClickOutside = (event) => {
  if (
    !event.target.closest(".mobile-menu") &&
    !event.target.closest(".toggle-button")
  ) {
    closeMobileMenu();
  }
};

// Handle scroll for sticky menu
const handleScroll = () => {
  stickyMenu.value = window.scrollY > 0;
};

// Use i18n for translations via helper
// Untuk kompatibilitas, gunakan helper function
const locale = ref(getLocale());

// Fungsi untuk log yang lebih aman
const logTranslationInfo = () => {
  console.log("HeaderFe.vue: active locale:", getLocale());
  console.log(
    "HeaderFe.vue: t(menu.information):",
    translationHelper("menu.information")
  );
};

// Panggil log hanya jika bukan production
if (process.env.NODE_ENV !== "production") {
  logTranslationInfo();
}

// Menggunakan fungsi tWithFallback dari helper
const tWithFallback = twf;

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("scroll", handleScroll);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <div>
    <header
      class="fixed top-0 left-0 w-full h-16 lg:h-16 transition-all duration-300 ease-in-out bg-white z-[99999] shadow"
      :class="{ 'shadow-sm': stickyMenu }">
      <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 lg:flex items-center justify-between relative h-full">
        <div class="flex items-center justify-between w-full h-full lg:w-3/12">
          <Link :href="route('home')" class="flex items-center h-full gap-3 py-2">
          <ApplicationLogo class="w-10 h-10 text-dark" />
          <div class="flex flex-col justify-center">
            <h1 class="hidden text-base font-bold text-maroon-800 sm:block font-roboto-slab mobile-font">
              {{ $page.props.apps.namaapps }}
            </h1>
            <h1 class="text-xs font-bold text-maroon-800 sm:hidden font-roboto-slab mobile-font">
              {{ $page.props.apps.namaapps }}
            </h1>
          </div>
          </Link>

          <!-- Hamburger Toggle BTN -->
          <button id="menuToggler" aria-label="button for menu toggle" class="block lg:hidden toggle-button"
            @click="toggleMobileMenu">
            <span class="relative block w-6 h-6 cursor-pointer">
              <span class="absolute right-0 block w-full h-full">
                <span
                  class="block relative top-0 left-0 bg-gray-800 rounded-sm w-0 h-0.5 my-1 ease-in-out duration-200 delay-0"
                  :class="{ '!w-full delay-300': !isMobileMenuVisible }"></span>
                <span
                  class="block relative top-0 left-0 bg-gray-800 rounded-sm w-0 h-0.5 my-1 ease-in-out duration-200 delay-150"
                  :class="{ '!w-full delay-400': !isMobileMenuVisible }"></span>
                <span
                  class="block relative top-0 left-0 bg-gray-800 rounded-sm w-0 h-0.5 my-1 ease-in-out duration-200 delay-200"
                  :class="{ '!w-full delay-500': !isMobileMenuVisible }"></span>
              </span>
              <span class="absolute right-0 block w-full h-full rotate-45">
                <span
                  class="block bg-gray-800 rounded-sm ease-in-out duration-200 delay-300 absolute left-2.5 top-0 w-0.5 h-full"
                  :class="{ '!h-0 delay-0': !isMobileMenuVisible }"></span>
                <span
                  class="block bg-gray-800 rounded-sm ease-in-out duration-200 delay-400 absolute left-0 top-2.5 w-full h-0.5"
                  :class="{ '!h-0 delay-200': !isMobileMenuVisible }"></span>
              </span>
            </span>
          </button>
        </div>

        <!-- Mobile Menu Start -->
        <div
          class="mobile-menu fixed inset-0 bg-black bg-opacity-40 z-[10000] transition-opacity duration-300 lg:hidden"
          v-if="isMobileMenuVisible">
          <div class="relative w-4/5 h-full max-w-xs p-6 overflow-y-auto bg-white shadow-lg" @click.stop>
            <button class="absolute text-gray-600 top-4 right-4 hover:text-dark focus:outline-none"
              @click="closeMobileMenu" aria-label="Close menu">
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
            <nav>
              <ul class="flex flex-col gap-4 mt-8">
                <!-- Language switcher for mobile -->
                <li class="mb-2">
                  <div class="px-2">
                    <LanguageSwitcher />
                  </div>
                </li>

                <li>
                  <Link :href="route('home')" class="block px-2 py-2 rounded hover:bg-gray-100 mobile-font"
                    @click="closeMobileMenu">{{ tWithFallback("menu.home", "Beranda") }}</Link>
                </li>
                <li>
                  <Link :href="route('profil')" class="block px-2 py-2 rounded hover:bg-gray-100 mobile-font"
                    @click="closeMobileMenu">{{ tWithFallback("menu.about", "Tentang Kami") }}</Link>
                </li>

                <li>
                  <button class="flex items-center justify-between w-full px-2 py-2 rounded hover:bg-gray-100"
                    @click="toggleMobileDropdown('formasi')">
                    <span>{{
                      tWithFallback("menu.formation", "Formasi")
                    }}</span>
                    <svg class="w-4 h-4 ml-2 transition-transform" :class="{
                      'rotate-180': activeMobileDropdownId === 'formasi',
                    }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <ul v-if="activeMobileDropdownId === 'formasi'" class="flex flex-col gap-2 pl-4 mt-2">

                    <li>
                      <Link :href="route('dosenjurusan')" class="block px-2 py-2 rounded hover:bg-gray-100"
                        @click="closeMobileMenu">{{ tWithFallback("menu.lecturers", "Dosen") }}</Link>
                    </li>
                  </ul>
                </li>
                <li>
                  <button class="flex items-center justify-between w-full px-2 py-2 rounded hover:bg-gray-100"
                    @click="toggleMobileDropdown('akademik')">
                    <span>{{
                      tWithFallback("menu.academic", "Akademik")
                    }}</span>
                    <svg class="w-4 h-4 ml-2 transition-transform" :class="{
                      'rotate-180': activeMobileDropdownId === 'formasi',
                    }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <ul v-if="activeMobileDropdownId === 'akademik'" class="flex flex-col gap-2 pl-4 mt-2">
                    <li>
                      <Link :href="route('dokumen')" class="block px-2 py-2 rounded hover:bg-gray-100"
                        @click="closeMobileMenu">{{
                          tWithFallback(
                            "menu.selfEvaluation",
                            "Dokumen Evaluasi Diri"
                          )
                        }}</Link>
                    </li>
                    <li>
                      <Link :href="route('dokumen')" class="block px-2 py-2 rounded hover:bg-gray-100"
                        @click="closeMobileMenu">{{
                          tWithFallback(
                            "menu.performanceDoc",
                            "Dokumen Kinerja"
                          )
                        }}</Link>
                    </li>
                    <!-- <li>
                      <Link
                        :href="route('grafik.frontend')"
                        class="block px-2 py-2 rounded hover:bg-gray-100"
                        @click="closeMobileMenu"
                        >Grafik Mahasiswa</Link
                      >
                    </li> -->
                  </ul>
                </li>
                <li>
                  <Link :href="route('informasi')" class="block px-2 py-2 rounded hover:bg-gray-100"
                    @click="closeMobileMenu">{{ tWithFallback("menu.information", "Informasi") }}</Link>
                </li>
                <li>
                  <Link :href="route('galeri')" class="block px-2 py-2 rounded hover:bg-gray-100"
                    @click="closeMobileMenu">{{ tWithFallback("menu.gallery", "Galeri") }}</Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        <!-- Mobile Menu End -->

        <!-- Desktop Menu -->
        <div class="items-center justify-between invisible w-full h-0 lg:w-9/12 lg:h-auto lg:visible lg:flex" :class="{
          'visible! bg-white shadow-lg relative h-auto! max-h-[400px] overflow-y-scroll rounded-md mt-4 p-7.5':
            isMobileMenuVisible,
        }" style="overflow: visible; z-index: inherit">
          <!-- Main Nav Start -->

          <!-- Main Nav End -->

          <!-- Language Switcher (visible on medium and smaller screens) -->

        </div>
        <!-- Desktop Language Switcher: move to far right -->

      </div>
    </header>
  </div>
</template>

<style scoped>
.dropdown {
  display: none;
  flex-direction: column;
  position: absolute;
  left: 0;
  top: 100%;
  min-width: 200px;
  background-color: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 0.375rem;
  padding: 0.5rem;
  z-index: 50;
}

.group:hover .dropdown {
  display: flex;
}

.nav__menu {
  transition: all 0.3s ease-in-out;
}

/* Mobile-specific font styling */
.mobile-font {
  /* Mobile font (default) */
  font-family: "Roboto Slab", "Inter", -apple-system, BlinkMacSystemFont,
    "Segoe UI", "Roboto", sans-serif;
  font-weight: bold;
}

/* Desktop font styling */
@media (min-width: 640px) {
  .mobile-font {
    font-family: "Roboto Slab", "Roboto", "Segoe UI", -apple-system,
      BlinkMacSystemFont, sans-serif;
    font-weight: inherit;
  }
}
</style>
